/**
 * Stores home at /stores — the landing page for store-only accounts (and a
 * plain tile menu for anyone else who navigates here).
 *
 * Large-touch-target tiles to the store tools: the daily routine, the store
 * thread, and Inventory Count. Store-only users land here from "/" (Home
 * redirects them) and live inside the dedicated store shell (no department
 * sidebar), so this page IS their Cockpit.
 */

import { Link } from 'react-router-dom';
import { CalendarCheck, ClipboardList, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useSession } from '@/lib/useSession';
import { useStoreMe, hasStoresStore, hasStoresHq } from '@/lib/useStores';
import { StoresHeader, AccessDeniedCard } from './storesShared';

const TILES: Array<{ to: string; icon: LucideIcon; title: string; desc: string }> = [
  {
    to: '/stores/today',
    icon: CalendarCheck,
    title: 'Today',
    desc: 'Opening, midday, and closing check-ins.',
  },
  {
    to: '/stores/inventory',
    icon: ClipboardList,
    title: 'Inventory Count',
    desc: 'Count shelves and receive orders.',
  },
];

export function StoreHome() {
  const { user } = useSession();
  const canView = hasStoresStore(user?.permissions, user?.superAdmin);
  const isHq = hasStoresHq(user?.permissions, user?.superAdmin);
  // Store users get their store name in the header; HQ visitors just see the menu.
  const meQ = useStoreMe();
  const storeName = !isHq && !meQ.error ? meQ.data?.store?.store_name : undefined;

  if (user && !canView) return <AccessDeniedCard what="The store tools" />;

  return (
    <div className="mx-auto w-full max-w-lg">
      <StoresHeader title="Your store" subtitle={storeName} />
      <div className="space-y-3">
        {TILES.map(({ to, icon: Icon, title, desc }) => (
          <Link
            key={to}
            to={to}
            className="flex w-full items-center gap-4 rounded-xl border border-amp-line bg-amp-white p-4 no-underline hover:border-amp-olive dark:border-amp-dark-line dark:bg-amp-dark-surface dark:hover:border-amp-olive-dark"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amp-cream-warm text-amp-olive dark:bg-amp-dark-surface-warm dark:text-amp-olive-dark">
              <Icon className="h-6 w-6" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-base font-semibold text-amp-ink dark:text-amp-dark-ink">
                {title}
              </span>
              <span className="block text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {desc}
              </span>
            </span>
            <ChevronRight
              className="h-5 w-5 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted"
              aria-hidden
            />
          </Link>
        ))}
      </div>
    </div>
  );
}
